import { Badge, Button, Code, Group, NavLink, Paper, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { useNavItems } from "../../shared/layout/nav";
import { errorMessage } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { useSession } from "../../shared/session/session";
import { useStepUp } from "../../shared/session/stepup";
import { FormError, Notice } from "../../shared/ui/Page";
import { DeleteSection } from "./DeleteSection";
import { ExportSection } from "./ExportSection";
import { InviteSection } from "./InviteSection";
import { MpesaSection } from "./MpesaSection";
import { ReactivateBanner } from "./ReactivateBanner";
import { SubscriptionSection } from "./SubscriptionSection";
import { SuspendSection } from "./SuspendSection";
import { TestimonialSection } from "./TestimonialSection";

export function AccountPage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const me = useMe();
  const stepUp = useStepUp();
  const navItems = useNavItems();
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (session.status === "loading") return null;
  if (session.status === "signed-out") return <Navigate to="/signin" replace />;
  const m = me.data;
  const suspended = m?.status === "suspended";

  const reveal = async () => {
    setError(null);
    const token = await stepUp("reveal_code");
    if (!token) return;
    const r = await api.POST("/pseudo/reveal", { params: { header: { "X-Step-Up-Token": token } } });
    if (!r.data) return setError(errorMessage(r.error, t("error.generic")));
    setCode(r.data.code);
  };
  const status = (ok: boolean) => (
    <Badge color={ok ? "green" : "gray"}>{ok ? t("account.verified") : t("account.not_verified")}</Badge>
  );

  return (
    <Stack maw={640} mx="auto" py="xl" gap="lg">
      <Title order={1} size="h2">
        {t("account.title")}
      </Title>
      {suspended && <ReactivateBanner onReactivated={() => setNotice(t("reactivate.done"))} />}
      {notice && <Notice>{notice}</Notice>}
      {m && (
        <Table>
          <Table.Tbody>
            <Table.Tr>
              <Table.Th>{t("account.phone")}</Table.Th>
              <Table.Td data-testid="masked-phone">{m.masked_phone}</Table.Td>
              <Table.Td>{status(m.phone_verified)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th>{t("account.email")}</Table.Th>
              <Table.Td>{m.masked_email}</Table.Td>
              <Table.Td>{status(m.email_verified)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th>{t("account.identity")}</Table.Th>
              <Table.Td colSpan={2}>{m.verification_status}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}
      <Stack gap="sm">
        {code ? (
          <Group>
            <Text>{t("account.your_code")}</Text>
            <Code fz="xl" data-testid="revealed-code">
              {code}
            </Code>
          </Group>
        ) : (
          <>
            <Text size="sm">{m?.totp_enabled ? t("account.step_up_totp") : t("account.step_up")}</Text>
            <Button variant="light" onClick={reveal} w="fit-content">
              {t("account.show_code")}
            </Button>
          </>
        )}
        <FormError message={error} />
      </Stack>
      <SubscriptionSection />
      {!suspended && <MpesaSection />}
      <ExportSection />
      {!suspended && (
        <>
          <InviteSection />
          <TestimonialSection />
        </>
      )}
      <Stack gap="xs" component="nav" aria-labelledby="manage-title">
        <Title order={2} size="h4" id="manage-title">
          {t("account.manage_title")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {navItems.map((i) => (
            <Paper key={i.to} withBorder radius="md">
              <NavLink component={Link} to={i.to} label={i.label} description={i.description} />
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
      <Group>
        <Button
          variant="default"
          onClick={async () => {
            await session.signOut();
            navigate("/signin");
          }}
        >
          {t("nav.sign_out")}
        </Button>
        <Button
          variant="subtle"
          color="red"
          onClick={async () => {
            await session.signOutEverywhere();
            navigate("/signin");
          }}
        >
          {t("account.sign_out_everywhere")}
        </Button>
      </Group>
      {!suspended && <SuspendSection />}
      <DeleteSection />
    </Stack>
  );
}
